// IE Hub - Date Field Component
// Enhanced date picker with validation and timezone support

import React, { forwardRef, useMemo, useState, useEffect } from 'react';
import {
  AdapterDateFns,
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
  SxProps,
  TextFieldProps,
  Theme,
  TimePicker,
  Typography,
  Box,
} from '@ui';
import { styled, alpha } from '@mui/material/styles';
import { IconButton, InputAdornment, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import { format, isAfter, isBefore, isValid, startOfDay } from 'date-fns';
import { calculateAge, getDobRangeFromAgeRules, parseDobToDate } from '@/shared/lib/utils/others';
import ConditionalContainer from '../layout/ConditionalContainer';

interface DateFieldValidation {
  required?: boolean;
  minAge?: number;
  maxAge?: number;
  allowedWeekdays?: number[];
  customValidator?: (date: Date) => string | null;
}

interface DateFieldProps {
  type?: 'date' | 'time' | 'datetime';
  label?: string;
  value?: Date | string | null;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  format?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
  validation?: DateFieldValidation;
  onValidate?: (isValid: boolean, error: string) => void;
  sx?: SxProps<Theme>;
  error?: boolean;
  helperText?: string;
  textFieldProps?: Partial<TextFieldProps>;
  onChange?: (event: { target: { value: Date | null; name?: string } }) => void;
  name?: string;
  // Custom UI blocks like TextField
  helperTop?: string | undefined;        // Above input
  helperAttached?: string | undefined;   // Attached block inside container
  helperBottom?: string | undefined;     // Below input
  showErrorBelow?: boolean;  // controls whether error renders beneath input
  // ConditionalContainer integration
  title?: string;
  description?: string;
  containerVariant?: 'paper' | 'outlined' | 'plain';
  showErrorInContainer?: boolean;
  // Validation timing control
  deferValidation?: boolean; // Only show errors after first blur or external error prop
  // LogRocket privacy masking - when true, adds data-private attribute to mask PII in recordings
  // Defaults to true for security; set to false for non-PII fields
  dataPrivate?: boolean;
  [key: string]: unknown;
}

// Styled components matching TextField
const Wrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const AttachedBlock = styled('div')(({ theme }: { theme: Theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem'
}));

const DateField = forwardRef<HTMLDivElement, DateFieldProps>(({
  type = 'date',
  label: fieldLabel,
  value,
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  required = false,
  disabled = false,
  format: dateFormat,
  minDate,
  maxDate,
  validation,
  onValidate,
  sx = {},
  error,
  helperText,
  textFieldProps,
  onChange,
  name,
  // New props like TextField
  helperTop,
  helperAttached,
  helperBottom,
  showErrorBelow = true,
  title,
  description,
  containerVariant = 'plain',
  showErrorInContainer = true,
  deferValidation = false,
  // LogRocket privacy masking - defaults to true
  dataPrivate = true,
  ...otherProps
}, ref) => {
  const [validationError, setValidationError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [hasExternalError, setHasExternalError] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Close the date picker when the component becomes disabled (e.g., when parent dialog closes)
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  // Track submit attempt via external error prop only (helper text should not trigger)
  useEffect(() => {
    setHasExternalError(Boolean(error));
  }, [error]);

  // Clear internal validation error when external error is cleared and field changes
  useEffect(() => {
    if (!error && hasExternalError && validationError) {
      // Once external error is cleared, allow internal validation to take over
      setValidationError('');
    }
  }, [error, hasExternalError, validationError]);

  // Default formats based on type
  const defaultFormats: Record<string, string> = {
    date: 'dd/MM/yyyy',
    time: 'HH:mm',
    datetime: 'dd/MM/yyyy HH:mm'
  };

  const fieldType = typeof type === 'string' && type in defaultFormats ? type : 'date';
  const actualFormat: string = typeof dateFormat === 'string' && dateFormat.length > 0
    ? dateFormat
    : (defaultFormats[fieldType as keyof typeof defaultFormats] || defaultFormats.date) as string;

  // Parse value to Date object
  const parseValue = (val: unknown) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (typeof val === 'string') {
      const parsed = parseDobToDate(val);
      return parsed && isValid(parsed) ? parsed : null;
    }
    return null;
  };

  const dateValue = parseValue(value);

  // Compute bounded date range from minAge/maxAge (if provided)
  const { computedMinDate, computedMaxDate } = useMemo(() => {
    const rules: DateFieldValidation = validation || {};
    const out: { computedMinDate?: Date; computedMaxDate?: Date } = {};

    // For DOB: max selectable date should be today (no future birthdays)
    const today = startOfDay(new Date());
    out.computedMaxDate = today;

    // Interpret minAge/maxAge in whole-years (floor) to match legacy behavior:
    // maxAge=105 allows up to 105y 11m 29d; maxAge=15 allows up to 15y 11m 29d, etc.
    const range = getDobRangeFromAgeRules({
      ...(typeof rules.minAge === 'number' && Number.isFinite(rules.minAge)
        ? { minAgeYears: rules.minAge }
        : {}),
      ...(typeof rules.maxAge === 'number' && Number.isFinite(rules.maxAge)
        ? { maxAgeYears: rules.maxAge }
        : {}),
      asOf: today,
    });

    if (range.minDob) out.computedMinDate = range.minDob;
    if (range.maxDob) out.computedMaxDate = range.maxDob;

    return out;
  }, [validation]);

  // Validation logic
  const validateInput = (selectedDate: Date | null) => {
    let error = '';
    const validationRules: DateFieldValidation = validation || {};

    // Required validation
    if ((validationRules.required || required) && !selectedDate) {
      error = 'This field is required';
    }

    if (selectedDate && isValid(selectedDate)) {
      // Min date validation
      if (minDate || computedMinDate) {
        const minDateObj = parseValue(minDate);
        const effMin = (computedMinDate || minDateObj) as Date | null;
        if (effMin && isBefore(selectedDate, effMin)) {
          error = `Date must be after ${format(effMin, actualFormat)}`;
        }
      }

      // Max date validation
      if (maxDate || computedMaxDate) {
        const maxDateObj = parseValue(maxDate);
        const effMax = (computedMaxDate || maxDateObj) as Date | null;
        if (effMax && isAfter(selectedDate, effMax)) {
          error = `Date must be before ${format(effMax, actualFormat)}`;
        }
      }

      // Custom validation rules
      if (validationRules.minAge) {
        const ageYears = calculateAge(selectedDate, startOfDay(new Date()));
        if (typeof ageYears !== 'number' || ageYears < validationRules.minAge) {
          error = `Must be at least ${validationRules.minAge} years old`;
        }
      }

      if (validationRules.maxAge) {
        const ageYears = calculateAge(selectedDate, startOfDay(new Date()));
        if (typeof ageYears !== 'number' || ageYears > validationRules.maxAge) {
          error = `Must be no more than ${validationRules.maxAge} years old`;
        }
      }

      // Weekday validation
      if (validationRules.allowedWeekdays) {
        const weekday = selectedDate.getDay();
        if (!validationRules.allowedWeekdays.includes(weekday)) {
          error = 'Selected day is not allowed';
        }
      }

      // Custom validation function
      if (validationRules.customValidator) {
        const customError = validationRules.customValidator(selectedDate);
        if (customError) {
          error = customError;
        }
      }
    }

    setValidationError(error);

    if (onValidate) {
      (onValidate as (isValid: boolean, error: string) => void)(error === '', error);
    }

    return error === '';
  };

  // Handle date change
  const handleChange = (newDate: Date | null) => {
    // Only run validation if not deferring, or after a submit attempt (external error present)
    const shouldValidateNow = !deferValidation || hasExternalError;

    if (shouldValidateNow) {
      const validationObj = validation && typeof validation === 'object' ? validation : {};
      if (Object.keys(validationObj).length > 0 || required) {
        validateInput(newDate);
      }
    }

    // Create synthetic event for consistency
    const syntheticEvent = {
      target: {
        value: newDate,
        name: name
      }
    };

    if (typeof onChange === 'function') {
      onChange(syntheticEvent);
    }
  };

  // Handle date error (parsing/validation errors)
  const handleError = (error: string | null) => {
    let errorMessage = '';

    // Normalize and coerce unknown values to string for robust matching
    const raw = (error ?? '').toString();
    const normalized = raw.trim().toLowerCase();

    // Any flavor of "invalid" should map to our friendly message
    const isInvalidKind =
      normalized === 'invaliddate' ||
      normalized === 'invalid date' ||
      normalized === 'invalid' ||
      normalized.includes('invalid');

    if (isInvalidKind) {
      errorMessage = 'Please enter a valid date';
    } else if (normalized === 'mindate') {
      const minDateParsed = (computedMinDate ?? (minDate ? parseValue(minDate) : null)) as Date | null;
      errorMessage = `Date must be after ${minDateParsed ? format(minDateParsed, actualFormat) : 'minimum date'}`;
    } else if (normalized === 'maxdate') {
      const maxDateParsed = (computedMaxDate ?? (maxDate ? parseValue(maxDate) : null)) as Date | null;
      errorMessage = `Date must be before ${maxDateParsed ? format(maxDateParsed, actualFormat) : 'maximum date'}`;
    } else if (normalized === 'disablefuture') {
      errorMessage = 'Future dates are not allowed';
    } else if (normalized === 'disablepast') {
      errorMessage = 'Past dates are not allowed';
    } else {
      // Fall back to provided string (empty if none)
      errorMessage = raw;
    }

    setValidationError(errorMessage);
  };

  // Select appropriate picker component
  const getPickerComponent = () => {
    switch (type) {
      case 'time':
        return TimePicker;
      case 'datetime':
        return DateTimePicker;
      default:
        return DatePicker;
    }
  };

  const PickerComponent = getPickerComponent();

  // Compute effective error state and message
  // When deferring validation, only show internal errors after submit
  const shouldShowInternalError = !deferValidation || hasExternalError;
  const effectiveError = error || (shouldShowInternalError && Boolean(validationError));

  // Normalize any variant of invalid date messages coming from external sources or internal state
  const normalizeDateErrorMessage = (raw?: string): string | undefined => {
    if (typeof raw !== 'string') return undefined;
    const text = raw.trim();
    if (text.length === 0) return undefined;
    const lower = text.toLowerCase();
    if (
      lower === 'invalid' ||
      lower === 'invaliddate' ||
      lower === 'invalid date' ||
      lower.includes('invalid date') ||
      lower.includes('invalid')
    ) {
      return 'Please enter a valid date';
    }
    return text;
  };

  const rawHelperText = ((error && helperText) || (shouldShowInternalError && validationError) || helperText || undefined) as string | undefined;
  const effectiveHelperText = normalizeDateErrorMessage(rawHelperText);

  // Common picker props
  const minDateValue = parseValue(minDate);
  const maxDateValue = parseValue(maxDate);

  const pickerProps = {
    value: dateValue,
    onChange: handleChange,
    onError: handleError,
    format: actualFormat,
    disabled: Boolean(disabled),
    ...(computedMinDate ? { minDate: computedMinDate } : (minDateValue ? { minDate: minDateValue } : {})),
    ...(computedMaxDate ? { maxDate: computedMaxDate } : (maxDateValue ? { maxDate: maxDateValue } : {})),
    open: isOpen,
    // Ignore open events from clicking/focusing the input; we open only via the icon
    onOpen: () => { },
    onClose: () => setIsOpen(false),
    slotProps: {
      // LogRocket privacy masking - mask the entire calendar popup (portal) for DOB fields
      desktopPaper: {
        ...(dataPrivate ? { 'data-private': true } : {}),
      },
      dialog: {
        ...(dataPrivate ? { 'data-private': true } : {}),
      },
      textField: {
        variant: variant as 'outlined' | 'filled' | 'standard',
        size: size as 'small' | 'medium',
        fullWidth: Boolean(fullWidth),
        error: Boolean(effectiveError),
        helperText: (effectiveError && !showErrorBelow) ? undefined : effectiveHelperText,
        required: Boolean(required),
        // Allow manual input; picker opens only via the icon button
        readOnly: false,
        // LogRocket privacy masking - add data-private attribute for PII fields (e.g., DOB)
        inputProps: {
          ...(dataPrivate ? { 'data-private': true } : {}),
        },
        InputProps: {
          // Explicitly render the calendar icon as endAdornment on all viewports
          // (mobile, tablet, desktop) so users always have a picker affordance.
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="open calendar"
                edge="end"
                disabled={Boolean(disabled)}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setIsOpen(true);
                }}
                sx={{
                  display: 'flex',
                  visibility: 'visible',
                  opacity: 1,
                }}
              >
                <DateRangeIcon />
              </IconButton>
            </InputAdornment>
          )
        },
        sx: {
          '& .MuiOutlinedInput-root': {
            height: '44px', // Set explicit height to 44px to match TextField
            display: 'flex',
            alignItems: 'center', // Center content vertically
            transition: 'all 0.2s ease-in-out',
            backgroundColor: 'background.paper',

            // Default border styling
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
              borderWidth: '1px',
            },

            // Error state styling - highest priority
            ...(Boolean(effectiveError) && {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'error.main !important',
                borderWidth: '2px !important',
              },
            }),

            '&:hover': {
              backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.02),
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: Boolean(effectiveError) ? 'error.main !important' : 'primary.main',
                borderWidth: '2px !important',
              },
            },

            '&.Mui-focused': {
              backgroundColor: 'background.paper',
              boxShadow: (theme: Theme) => `0 0 0 2px ${alpha(Boolean(effectiveError) ? theme.palette.error.main : theme.palette.primary.main, 0.2)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: Boolean(effectiveError) ? 'error.main !important' : 'primary.main !important',
                borderWidth: '2px !important',
              },
            },

            // MUI error class - ensure it works
            '&.Mui-error': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'error.main !important',
                borderWidth: '2px !important',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'error.main !important',
                borderWidth: '2px !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'error.main !important',
                borderWidth: '2px !important',
              },
            },

            '&.Mui-disabled': {
              backgroundColor: (theme: Theme) => alpha(theme.palette.action.disabled, 0.05),
            },
          },

          '& .MuiInputLabel-outlined': {
            '&.Mui-focused': {
              color: Boolean(effectiveError) ? 'error.main' : 'primary.main',
            },
            '&.Mui-error': {
              color: 'error.main',
            },
          },

          '& .MuiFormHelperText-root': {
            marginLeft: 0,
            marginTop: (theme: Theme) => theme.spacing(0.75),
            fontSize: '0.8125rem',
            transition: 'color 0.2s ease-in-out',
          },

          '& .MuiInputBase-input': {
            fontSize: size === 'small' ? '0.875rem' : '1rem',
            height: 'auto',
            padding: '12px 14px 10px 14px', // More padding on top to center text
            lineHeight: '1.2',
            boxSizing: 'border-box',
          },

          // Additional centering for the input wrapper
          '& .MuiInputBase-formControl': {
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
          },

          // Ensure placeholder is centered
          '& .MuiInputBase-input::placeholder': {
            opacity: 0.6,
            color: 'text.secondary',
            transform: 'translateY(-1px)', // Fine-tune vertical position
          },
        },
        ...(textFieldProps || {}),
      }
    },
    ...otherProps
  };

  // Content to render inside ConditionalContainer (matching TextField pattern)
  const dateFieldContent = (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Wrapper>
        {fieldLabel && !title && (
          <Typography variant="subtitle1" sx={{ mb: 0.75, fontWeight: 600, color: 'primary.main', fontSize: isMobile ? '14px' : '16px', lineHeight: isMobile ? '20px' : '22px' }}>
            {fieldLabel}
            {((validation as DateFieldValidation)?.required || required) && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
            )}
          </Typography>
        )}
        {helperTop && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px' }}>
            {helperTop}
          </Typography>
        )}
        {/* Error message above input field */}
        {effectiveHelperText && effectiveError && !showErrorBelow && (
          <Typography
            variant="body2"
            color="error"
            sx={{
              mb: 1,
              fontSize: isMobile ? '12px' : '14px',
              lineHeight: isMobile ? '16px' : '20px',
              fontWeight: 500
            }}
          >
            {effectiveHelperText}
          </Typography>
        )}

        <PickerComponent
          ref={ref}
          label={type === 'time' && typeof fieldLabel === 'string' ? fieldLabel : undefined}
          {...pickerProps}
          slots={{
            openPickerIcon: DateRangeIcon,
          }}
          // Ensure the calendar icon is always visible and only opens on icon click
          slotProps={{
            ...pickerProps.slotProps,
            openPickerButton: {
              onClick: (e: React.MouseEvent) => {
                // Only open picker when clicking the icon button
                e.stopPropagation();
                setIsOpen(true);
              },
              sx: {
                display: 'flex !important', // Force display on all devices including mobile
                visibility: 'visible !important',
                opacity: 1,
                pointerEvents: 'auto',
                padding: '8px',
                marginRight: '-4px',
                color: 'action.active',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'primary.main',
                },
              }
            }
          }}
        />

        {helperAttached && (
          <AttachedBlock>{helperAttached}</AttachedBlock>
        )}
        {helperBottom && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: effectiveError ? 0.75 : 1.25, mb: 0, fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px' }}>
            {helperBottom}
          </Typography>
        )}
      </Wrapper>
    </LocalizationProvider>
  );

  const containerProps: any = {
    ifError: false, // Never show error in container
    variant: containerVariant,
    sx: {
      ...(sx as Record<string, unknown>),
      mb: isMobile ? 2.25 : 2.5,
      // Add red left border when there's a validation error
      ...(effectiveError && {
        borderLeft: '4px solid',
        borderLeftColor: 'error.main',
        paddingLeft: 2,
        paddingBottom: 0,
        minHeight: 'auto',
        height: 'fit-content',
        maxHeight: '90%',
      }),
      // Ensure consistent width regardless of error state
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 1.5,
      // Reduce overall container height when in error state
      ...(effectiveError && {
        paddingTop: 0,
        marginBottom: 0,
      })
    },
  };

  if (title || (fieldLabel && title !== undefined)) {
    containerProps.title = title || fieldLabel;
  }

  if (description) {
    containerProps.description = description;
  }

  return (
    <ConditionalContainer {...containerProps}>
      {dateFieldContent}
    </ConditionalContainer>
  );
});

DateField.displayName = 'DateField';

export default DateField;
